import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Warehouse, FolderTree, Package, AlertCircle, Pencil, Trash2, ChevronRight, X, Loader2 } from 'lucide-react';
import api from '../api/api';

export default function Inventario() {
  const [areas, setAreas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', area}
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/warehouse/areas'), api.get('/warehouse/summary')])
      .then(([a, s]) => { setAreas(a.data); setSummary(s.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: '', description: '' }); setError(''); setModal({ mode: 'create' }); };
  const openEdit = (area) => { setForm({ name: area.name, description: area.description || '' }); setError(''); setModal({ mode: 'edit', area }); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (modal.mode === 'edit') await api.put(`/warehouse/areas/${modal.area.id}`, form);
      else await api.post('/warehouse/areas', form);
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (area) => {
    if (!window.confirm(`¿Eliminar el área "${area.name}"? Debe estar vacía.`)) return;
    try {
      await api.delete(`/warehouse/areas/${area.id}`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'No se pudo eliminar el área');
    }
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Almacén</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {areas.length} área(s) · {summary?.total_items ?? 0} referencias · {summary?.total_low_stock ?? 0} bajo mínimos
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          data-testid="add-area-btn"
        >
          <Plus size={16} /> Nueva Área / Taller
        </button>
      </div>

      {summary && summary.total_low_stock > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="low-stock-summary">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {summary.total_low_stock} referencia(s) bajo mínimos
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {summary.areas.filter(a => a.low_stock_count > 0).map(a => (
                  <span key={a.area_id} className="text-xs bg-white border border-amber-200 text-amber-800 px-2 py-1 rounded-full"
                    data-testid={`low-stock-area-${a.area_id}`}>
                    {a.area_name}: <strong>{a.low_stock_count}</strong>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {areas.map(area => (
            <div
              key={area.id}
              className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#16a34a]/40 transition-all cursor-pointer overflow-hidden"
              onClick={() => navigate(`/inventario/area/${area.id}`)}
              data-testid={`area-card-${area.id}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#16a34a]/10 flex items-center justify-center flex-shrink-0">
                      <Warehouse size={18} className="text-[#16a34a]" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate" style={{ fontFamily: 'Barlow, sans-serif' }}>{area.name}</h3>
                      <p className="text-xs text-gray-400 truncate">{area.description || 'Sin descripción'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={e => { e.stopPropagation(); openEdit(area); }}
                      className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                      title="Editar área"
                      data-testid={`edit-area-${area.id}`}
                    ><Pencil size={14} /></button>
                    <button
                      onClick={e => { e.stopPropagation(); handleDelete(area); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Eliminar área"
                      data-testid={`delete-area-${area.id}`}
                    ><Trash2 size={14} /></button>
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-4 text-xs">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <FolderTree size={13} className="text-gray-400" /> {area.folders_count} carpeta(s)
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <Package size={13} className="text-gray-400" /> {area.items_count} referencia(s)
                  </span>
                  {area.low_stock_count > 0 && (
                    <span className="flex items-center gap-1 text-amber-700 font-medium">
                      <AlertCircle size={13} /> {area.low_stock_count} bajo mínimos
                    </span>
                  )}
                </div>
              </div>
              <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Ver carpetas</span>
                <ChevronRight size={14} className="text-gray-400 group-hover:text-[#16a34a] group-hover:translate-x-0.5 transition-all" />
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" data-testid="area-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
                {modal.mode === 'edit' ? 'Editar Área' : 'Nueva Área / Taller'}
              </h2>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600 p-1" data-testid="close-area-modal"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="area-modal-error">{error}</div>}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text" value={form.name} required autoFocus
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Taller de Maquinaria Móvil"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                  data-testid="area-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={form.description} rows={2}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"
                  data-testid="area-description-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]"
                  data-testid="save-area-btn">
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
